import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Car,
  Plus,
  Settings,
  User,
  Phone,
  FileText,
  Navigation,
  LayoutGrid,
  History,
  Wallet,
  Headset,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { socket } from "../api/socket";
import Loader from "../components/Loader2.jsx";

// --- 1. COMPACT CLOCK  ---
const LiveClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="text-end">
      <div
        className="text-white fw-bold"
        style={{
          fontFamily: "monospace",
          fontSize: "1.05rem",
          letterSpacing: "0.5px",
          lineHeight: 1,
        }}
      >
        {time.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}
      </div>
      <div
        className="text-white opacity-75 text-uppercase fw-bold mt-1"
        style={{ fontSize: "0.6rem", letterSpacing: "1px" }}
      >
        {time.toLocaleDateString([], {
          weekday: "short",
          day: "numeric",
          month: "short",
        })}
      </div>
    </div>
  );
};

// --- 2. HEADER COMPONENT  ---
const DashboardHeader = ({ profile }) => {
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <div
      className="position-relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
        borderRadius: "0 0 32px 32px",
        padding: "20px 20px 24px 20px",
        boxShadow: "0 10px 30px -10px rgba(49, 46, 129, 0.5)",
        marginBottom: "20px",
      }}
    >
      {/* Decorative background glow */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "180px",
          height: "180px",
          background:
            "radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)",
          top: "-30px",
          right: "-30px",
          filter: "blur(25px)",
        }}
      />

      {/* TOP ROW: Greeting & Clock */}
      <div className="d-flex justify-content-between align-items-start mb-3 position-relative z-index-1">
        <div
          className="fw-bold text-uppercase text-white opacity-75 mt-1"
          style={{
            fontSize: "0.65rem",
            letterSpacing: "1.5px",
          }}
        >
          {getGreeting()}
        </div>
        <LiveClock />
      </div>

      {/* BOTTOM ROW: Profile & Settings */}
      <div className="d-flex justify-content-between align-items-center position-relative z-index-1">
        <div className="d-flex align-items-center gap-3">
          {/* Avatar */}
          <div
            className="position-relative shadow-sm"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "18px",
              border: "2px solid rgba(255, 255, 255, 0.1)",
              padding: "2px",
              background: "rgba(255,255,255,0.05)",
            }}
          >
            <div className="w-100 h-100 rounded-4 overflow-hidden bg-white d-flex align-items-center justify-content-center">
              {profile.cust_img ? (
                <img
                  src={`${IMAGE_BASE_URL}${profile.cust_img}`}
                  alt="Profile"
                  className="w-100 h-100 object-fit-cover"
                />
              ) : (
                <div
                  className="w-100 h-100 d-flex align-items-center justify-content-center fw-bold text-white fs-4"
                  style={{ background: "#4f46e5" }}
                >
                  {profile.fullName
                    ? profile.fullName.charAt(0).toUpperCase()
                    : "U"}
                </div>
              )}
            </div>
          </div>

          {/* Name & Email */}
          <div>
            <h5
              className="fw-bold text-white mb-1 text-truncate"
              style={{
                fontSize: "1.15rem",
                maxWidth: "180px",
                letterSpacing: "-0.3px",
                lineHeight: "1.2",
              }}
            >
              {profile.fullName || "Guest User"}
            </h5>
            <div
              className="text-white small d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: "0.75rem", opacity: 0.8 }}
            >
              <Mail size={12} />
              <span className="text-truncate" style={{ maxWidth: "160px" }}>
                {profile.email || "Add your email"}
              </span>
            </div>
          </div>
        </div>

        {/* Settings Button */}
        <button
          onClick={() => navigate("/profile")}
          className="btn p-0 d-flex align-items-center justify-content-center header-action-btn shadow-sm"
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            color: "#ffffff",
          }}
        >
          <Settings size={18} strokeWidth={2} />
        </button>
      </div>

      <style>{`
        .header-action-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .header-action-btn:active {
          transform: scale(0.9);
          background: rgba(255, 255, 255, 0.25) !important;
        }
      `}</style>
    </div>
  );
};

// --- 3. QUICK ACTIONS ---
const QuickActions = ({ navigate }) => {
  const actions = [
    {
      id: 1,
      label: "Services",
      icon: LayoutGrid,
      path: "/services",
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      id: 2,
      label: "My Rides",
      icon: History,
      path: "/booking-history",
      color: "#8b5cf6",
      bg: "#f5f3ff",
    },
    {
      id: 3,
      label: "Payments",
      icon: Wallet,
      path: "/payment-history",
      color: "#10b981",
      bg: "#ecfdf5",
    },
    {
      id: 4,
      label: "Support",
      icon: Headset,
      path: "/contact-office",
      color: "#f59e0b",
      bg: "#fffbeb",
    },
  ];

  return (
    <div className="mb-4 d-flex justify-content-between px-3">
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <div
            key={action.id}
            className="d-flex flex-column align-items-center cursor-pointer quick-action-btn"
            onClick={() => navigate(action.path)}
            style={{ gap: "8px" }}
          >
            <div
              className="rounded-circle d-flex justify-content-center align-items-center shadow-sm icon-container"
              style={{
                width: "58px",
                height: "58px",
                backgroundColor: action.bg,
                transition: "transform 0.1s ease",
              }}
            >
              <Icon size={24} color={action.color} strokeWidth={2.2} />
            </div>
            <span
              className="fw-bold text-secondary"
              style={{ fontSize: "0.75rem", letterSpacing: "0.2px" }}
            >
              {action.label}
            </span>
          </div>
        );
      })}

      {/* Add this style block to handle native tap animations */}
      <style>{`
        .quick-action-btn:active .icon-container {
          transform: scale(0.90) !important;
        }
      `}</style>
    </div>
  );
};

// --- 4. MAIN PAGE ---
const DashboardPage = () => {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("customerToken"));
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ fullName: "Guest", cust_img: null });
  const [upcomingTrips, setUpcomingTrips] = useState([]);

  const nativeStyles = {
    page: { background: "#ffffff", paddingBottom: "50px", minHeight: "100vh" },
    // Soft Purple Compact Android Native Card
    nativeTripCard: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "20px",
      border: "none",
      boxShadow: "0 8px 24px rgba(109, 40, 217, 0.25)",
      color: "white",
      overflow: "hidden",
    },
  };

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    fetchDashboardData();

    if (socket) {
      const handleDriverUpdate = (data) => {
        toast.success(`Update: ${data.message}`);
        fetchDashboardData();
      };
      socket.on("driver_assigned", handleDriverUpdate);
      return () => socket.off("driver_assigned", handleDriverUpdate);
    }
  }, [token, navigate]);

  // Safely parse ISO time into readable 12-hour format
  const formatTimeFromISO = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes("T")) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    }
    return timeStr;
  };

  // DYNAMIC STATUS LOGIC: Checks if current time is past the pickup time
  const getDynamicStatus = (dateStr, timeStr, enqStatus) => {
    // 1. If backend explicitly marked it as completed
    if (enqStatus !== 1) return "Completed";

    // 2. Start with the Journey Date
    let tripDateTime = new Date(dateStr);

    // 3. Inject the exact Hours and Minutes into that Date
    if (timeStr) {
      if (timeStr.includes("T")) {
        // If the time field is a full ISO string (e.g., "2026-04-03T12:00:00.000Z")
        const timeObj = new Date(timeStr);
        tripDateTime.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0);
      } else {
        // If the time field is a standard SQL time string (e.g., "12:00:00" or "14:30")
        const [hours, minutes] = timeStr.split(":");
        if (hours !== undefined && minutes !== undefined) {
          tripDateTime.setHours(
            parseInt(hours, 10),
            parseInt(minutes, 10),
            0,
            0,
          );
        }
      }
    }
    // 4. Compare the stitched Date with the exact current time
    const now = new Date();

    if (now >= tripDateTime) {
      return "Ongoing";
    }
    return "Scheduled";
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [profileRes, tripsRes] = await Promise.all([
        api.get("/customer/profile", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/bookingRoutes/dashboardtrip", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setProfile(profileRes.data || { fullName: "Guest", cust_img: null });

      const trips = tripsRes.data?.data || tripsRes.data || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const formattedTrips = trips
        .map((trip) => ({
          id: trip.enq_id,
          reference_no:
            trip.reference_no || trip.enqref_no || `EN${trip.enq_id}`,
          name: trip.customer_name || "Guest",
          pickup:
            trip.pickup_location || trip.pick_cityName || "Unknown Pickup",
          drop: trip.drop_address || trip.dropoff_location || "N/A",
          date: trip.Journey_date,
          time: formatTimeFromISO(trip.Pickup_time),
          // Apply dynamic status calculation here
          status: getDynamicStatus(
            trip.Journey_date,
            trip.Pickup_time,
            trip.enq_status,
          ),
          driver_name: trip.driver_name,
          driver_phone: trip.driver_phno,
        }))
        .filter((trip) => {
          if (trip.status === "Completed") return false;
          if (trip.date) {
            const tripDate = new Date(trip.date);
            tripDate.setHours(0, 0, 0, 0);
            return tripDate >= today;
          }
          return false;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setUpcomingTrips(formattedTrips);
    } catch (err) {
      console.error("Dashboard fetch error:", err.response || err);
      toast.error("Failed to fetch trips");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={nativeStyles.page}>
      <DashboardHeader profile={profile} />

      <QuickActions navigate={navigate} />

      <div className="container px-2">
        {/* Book Ride Banner - Premium Native Redesign */}
        <div
          className="card border-0 mb-4 position-relative overflow-hidden book-ride-card"
          style={{
            borderRadius: "24px",
            background: "#0f172a", // Deep midnight slate
            boxShadow: "0 16px 32px rgba(15, 23, 42, 0.25)",
            transform: "translateZ(0)", // Hardware acceleration for smooth rendering
          }}
        >
          {/* Animated Glow Orbs for Depth */}
          <div
            className="position-absolute rounded-circle glow-orb"
            style={{
              width: "180px",
              height: "180px",
              background:
                "radial-gradient(circle, #3b82f6 0%, transparent 70%)",
              top: "-50px",
              right: "-20px",
              opacity: 0.6,
              filter: "blur(40px)",
            }}
          />
          <div
            className="position-absolute rounded-circle glow-orb-2"
            style={{
              width: "220px",
              height: "220px",
              background:
                "radial-gradient(circle, #8b5cf6 0%, transparent 70%)",
              bottom: "-80px",
              right: "-40px",
              opacity: 0.5,
              filter: "blur(50px)",
            }}
          />

          {/* Decorative Car Watermark */}
          <div
            className="position-absolute"
            style={{
              right: "-15px",
              bottom: "5px",
              opacity: 0.1,
              transform: "rotate(-10deg)",
            }}
          >
            <Car size={160} color="#ffffff" strokeWidth={1} />
          </div>

          <div className="card-body p-4 position-relative z-index-1 d-flex flex-column justify-content-between">
            <div className="mb-4">
              {/* Premium Tag */}

              <h3
                className="fw-bold text-white mb-1"
                style={{ letterSpacing: "-0.5px", fontSize: "1.75rem" }}
              >
                Where to?
              </h3>
              <p
                className="text-white-50 small mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                Book your safe & luxury ride today.
              </p>
            </div>

            {/* Asymmetrical Faux-Input Button */}
            <button
              className="btn w-100 rounded-pill d-flex align-items-center justify-content-between p-1 ps-4 shadow-lg native-book-btn"
              onClick={() => navigate("/book-ride")}
              style={{
                background: "#ffffff",
                border: "none",
                height: "60px",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div style={{ color: "#3b82f6" }}>
                  <Plus size={22} strokeWidth={3} />
                </div>
                <span
                  className="fw-bold text-dark"
                  style={{ fontSize: "1rem" }}
                >
                  Book New Ride
                </span>
              </div>
              {/* Circular Icon Accent inside the button */}
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-1"
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                }}
              >
                <Car size={20} className="text-white" />
              </div>
            </button>
          </div>
        </div>

        {/* Upcoming Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <h6 className="fw-bold m-0 text-dark fs-5">
            Upcoming
            {upcomingTrips.length > 0 && (
              <span
                className="ms-2 badge rounded-pill"
                style={{ background: "#2563eb", fontSize: "0.75rem" }}
              >
                {upcomingTrips.length}
              </span>
            )}
          </h6>
          <span
            className="text-primary fw-bold cursor-pointer small"
            onClick={() => navigate("/booking-history")}
          >
            View All
          </span>
        </div>

        {/* Trips list */}
        {upcomingTrips.length > 0 ? (
          upcomingTrips.map((trip, index) => {
            const tripDate = trip.date ? new Date(trip.date) : null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isToday =
              tripDate && tripDate.setHours(0, 0, 0, 0) === today.getTime();

            return (
              <div
                key={`${trip.id}-${index}`}
                className="card mb-4"
                style={nativeStyles.nativeTripCard}
              >
                <div className="card-body p-3">
                  {/* Top Row: Date, Time, Status */}
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      {trip.date && (
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h6 className="fw-bold text-white m-0 fs-5">
                            {new Date(trip.date).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </h6>
                          {isToday && (
                            <span
                              className="badge rounded-pill bg-white text-dark fw-bold"
                              style={{ fontSize: "0.6rem", padding: "4px 8px" }}
                            >
                              Today
                            </span>
                          )}
                        </div>
                      )}
                      <span className="text-white-50 small fw-semibold d-flex align-items-center gap-1">
                        <Clock size={13} /> {trip.time}
                      </span>
                    </div>
                    {/* Badge color dynamically changes based on status */}
                    <span
                      className="badge rounded-pill fw-bold shadow-sm"
                      style={{
                        backgroundColor:
                          trip.status === "Ongoing"
                            ? "#fbbf24"
                            : "rgba(255,255,255,0.2)",
                        color:
                          trip.status === "Ongoing" ? "#78350f" : "#ffffff",
                        padding: "8px 12px",
                        fontSize: "0.7rem",
                        backdropFilter: "blur(5px)",
                      }}
                    >
                      {trip.status}
                    </span>
                  </div>

                  {/* Driver Section - Glassmorphism */}
                  <div
                    className="d-flex align-items-center gap-3 mb-3 p-2 rounded-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center shadow-sm"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <User
                        size={18}
                        className="text-primary"
                        style={{ color: "#6d28d9" }}
                      />
                    </div>
                    <div>
                      <div
                        className="text-white-50 fw-bold mb-1"
                        style={{ fontSize: "0.6rem", letterSpacing: "1px" }}
                      >
                        DRIVER
                      </div>
                      <div
                        className="fw-bold text-white text-capitalize"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {trip.driver_name || "Assigning Soon..."}
                      </div>
                      {trip.driver_phone && (
                        <div
                          className="d-flex align-items-center gap-1 text-white-50 fw-semibold mt-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <Phone size={11} /> {trip.driver_phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Route Timeline */}
                  <div className="position-relative ms-2 mt-1">
                    {trip.drop && (
                      <div
                        className="position-absolute"
                        style={{
                          left: "5px",
                          top: "16px",
                          bottom: "16px",
                          borderLeft: "2px dashed rgba(255,255,255,0.3)",
                          zIndex: 0,
                        }}
                      />
                    )}

                    {/* Pickup */}
                    <div className="d-flex position-relative mb-2">
                      <div
                        className="mt-1 me-3 bg-white"
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          border: "3px solid #4ade80", // Soft Green
                          zIndex: 1,
                        }}
                      />
                      <div className="flex-grow-1">
                        <div
                          className="text-white-50 fw-bold mb-1"
                          style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}
                        >
                          PICKUP
                        </div>
                        <div
                          className="fw-bold text-white text-truncate"
                          style={{ fontSize: "0.85rem", maxWidth: "260px" }}
                        >
                          {trip.pickup}
                        </div>
                      </div>
                    </div>

                    {/* Drop */}
                    <div className="d-flex position-relative">
                      <div
                        className="mt-1 me-3 bg-white"
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          border: "3px solid #f87171", // Soft Red
                          zIndex: 1,
                        }}
                      />
                      <div className="flex-grow-1">
                        <div
                          className="text-white-50 fw-bold mb-1"
                          style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}
                        >
                          DROP
                        </div>
                        <div
                          className="fw-bold text-white text-truncate"
                          style={{ fontSize: "0.85rem", maxWidth: "260px" }}
                        >
                          {trip.drop}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- Action Buttons --- */}
                <div
                  className="p-3"
                  style={{ background: "rgba(0, 0, 0, 0.1)" }}
                >
                  <div className="d-flex gap-2">
                    <button
                      className="btn flex-grow-1 rounded-pill fw-bold d-flex align-items-center justify-content-center gap-1"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.15)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        fontSize: "0.8rem",
                        padding: "8px",
                      }}
                      onClick={() => navigate(`/enquiry/${trip.reference_no}`)}
                    >
                      <FileText size={15} /> Enquiry
                    </button>

                    <button
                      className="btn flex-grow-1 rounded-pill fw-bold d-flex align-items-center justify-content-center gap-1"
                      style={{
                        backgroundColor: "#ffffff",
                        color: "#6d28d9",
                        border: "none",
                        fontSize: "0.8rem",
                        padding: "8px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                      }}
                      onClick={() =>
                        navigate(`/active-trip/${trip.reference_no}`)
                      }
                    >
                      <Navigation size={15} /> Go Trip
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-5 rounded-4 bg-white border-0 shadow-sm mt-3">
            <div
              className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: "60px", height: "60px" }}
            >
              <Car size={24} className="text-muted opacity-50" />
            </div>
            <h6 className="fw-bold text-dark mb-1">No active rides</h6>
            <p className="text-muted small mb-0">
              Your upcoming bookings will appear here.
            </p>
          </div>
        )}
      </div>
      <style>{`
          .native-book-btn {
            transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s;
          }
          /* This creates the native "squish" feeling when a mobile user taps the button */
          .native-book-btn:active {
            transform: scale(0.96); 
            background: #f8fafc !important;
          }
          /* Smooth, cinematic breathing effect for the background colors */
          .glow-orb {
            animation: pulseGlow 4s infinite alternate ease-in-out;
          }
          .glow-orb-2 {
            animation: pulseGlow 6s infinite alternate-reverse ease-in-out;
          }
          @keyframes pulseGlow {
            0% { transform: scale(1) translate(0, 0); opacity: 0.4; }
            100% { transform: scale(1.1) translate(-10px, 10px); opacity: 0.7; }
          }
        `}</style>
    </div>
  );
};

export default DashboardPage;